import { getStarterAiChoices } from "../../connectCatalog";

export type SetupStepId = "name" | "focus" | "ai" | "accounts" | "budget" | "methodology";
export type TourStepId =
  | "click"
  | "life"
  | "projects"
  | "workstreams"
  | "apps"
  | "files"
  | "settings";

export type WorkspaceTourSurface =
  | "life"
  | "projects"
  | "workstreams"
  | "apps"
  | "files"
  | "settings";

export interface QuickChoice {
  id: string;
  label: string;
  description?: string;
}

export interface SetupStepDefinition {
  id: SetupStepId;
  title: string;
  prompt: string;
}

export interface GuidedTourStep {
  id: TourStepId;
  title: string;
  body: string;
  actionLabel: string;
  surface?: WorkspaceTourSurface;
}

export const SETUP_STEPS: SetupStepDefinition[] = [
  {
    id: "name",
    title: "Name",
    prompt: "What should I call myself?",
  },
  {
    id: "focus",
    title: "Day one focus",
    prompt: "What do you want me to be especially good at on day one?",
  },
  {
    id: "ai",
    title: "AI",
    prompt: "Want to add your own AI right now?",
  },
  {
    id: "accounts",
    title: "Accounts",
    prompt: "Want to connect email or calendar now?",
  },
  {
    id: "budget",
    title: "Budget",
    prompt: "What daily spend feels comfortable?",
  },
  {
    id: "methodology",
    title: "Working style",
    prompt: "A few quick questions about how you like to work.",
  },
];

export const FOCUS_CHOICES: QuickChoice[] = [
  { id: "sales", label: "Sales", description: "Lead research, outreach, CRM work" },
  { id: "content", label: "Content", description: "Drafting, editing, publishing" },
  { id: "development", label: "Development", description: "Code, docs, debugging" },
  { id: "research", label: "Research", description: "Deep dives, reports, synthesis" },
  { id: "general", label: "General help", description: "A little bit of everything" },
];

export const AI_CHOICES = getStarterAiChoices();

export const ACCOUNT_CHOICES: QuickChoice[] = [
  { id: "connect", label: "Connect now", description: "Set up email or calendar during setup" },
  { id: "later", label: "Later", description: "Finish setup now and connect accounts later" },
];

export const BUDGET_CHOICES: QuickChoice[] = [
  { id: "10", label: "$10/day", description: "Light usage" },
  { id: "25", label: "$25/day", description: "Regular usage" },
  { id: "50", label: "$50/day", description: "Heavy usage" },
  { id: "none", label: "No limit", description: "I will manage spending myself" },
];

export const METHODOLOGY_TASKING_CHOICES: QuickChoice[] = [
  {
    id: "batch",
    label: "Batch tasks",
    description: "I like big focused blocks on one thing at a time",
  },
  {
    id: "interleave",
    label: "Interleave",
    description: "I switch context often and react as things come in",
  },
  { id: "mixed", label: "A bit of both", description: "Depends on the day" },
];

export const METHODOLOGY_PROJECT_CAPACITY_CHOICES: QuickChoice[] = [
  { id: "1-3", label: "1–3 open projects", description: "I like to keep focus tight" },
  { id: "4-7", label: "4–7", description: "Comfortable middle" },
  { id: "8+", label: "8 or more", description: "I juggle a lot" },
];

export const METHODOLOGY_REVIEW_CADENCE_CHOICES: QuickChoice[] = [
  { id: "yes_routinely", label: "Yes, routinely", description: "I keep a weekly review" },
  { id: "sometimes", label: "Sometimes", description: "When the week calls for it" },
  { id: "no", label: "No", description: "Skip the next-week plan suggestions" },
];

export const GUIDED_TOUR_STEPS: GuidedTourStep[] = [
  {
    id: "click",
    title: "Click anywhere",
    body: "You can click almost anything here and ask me to change it right there.",
    actionLabel: "Next",
  },
  {
    id: "life",
    title: "Life",
    body: "Use Life for notes, plans, and routines. I can turn a messy thought into something usable.",
    actionLabel: "Open Life",
    surface: "life",
  },
  {
    id: "projects",
    title: "Projects",
    body: "Projects are the bigger things we plan and keep track of together.",
    actionLabel: "Open Projects",
    surface: "projects",
  },
  {
    id: "workstreams",
    title: "Workstreams",
    body: "Workstreams are the ongoing loops I keep moving for you, like monitoring, follow-up, or research.",
    actionLabel: "Open Workstreams",
    surface: "workstreams",
  },
  {
    id: "apps",
    title: "Apps",
    body: "Apps are focused tools I can build when you want a more structured way to work.",
    actionLabel: "Open Apps",
    surface: "apps",
  },
  {
    id: "files",
    title: "Files",
    body: "Files is where you can find what I made for you and add files that help me help you better.",
    actionLabel: "Open Files",
    surface: "files",
  },
  {
    id: "settings",
    title: "Settings",
    body: "Settings is where you can change AI, accounts, spending, and limits later.",
    actionLabel: "Open Settings",
    surface: "settings",
  },
];

export function getFocusFollowUp(choiceId: string, customText?: string): string {
  if (customText?.trim()) {
    return "Perfect. I’ll use that as my day-one focus and adjust the workspace around it.";
  }
  switch (choiceId) {
    case "sales":
      return "Great. I’ll lean into lead research, outreach, and follow-up.";
    case "content":
      return "Perfect. I’ll bias toward drafting, editing, and publishing support.";
    case "development":
      return "Great. I’ll focus on code, debugging, docs, and reviews.";
    case "research":
      return "Good fit. I’ll focus on synthesis, evidence, and crisp summaries.";
    default:
      return "Sounds good. I’ll stay flexible until your first real task gives me more shape.";
  }
}

export function getBudgetFollowUp(choiceId: string): string {
  if (choiceId === "none") {
    return "Okay. I won’t enforce a daily cap.";
  }
  return `Okay. I’ll keep the fallback budget inside ${choiceId.startsWith("$") ? choiceId : `$${choiceId}/day`}.`;
}
